import type { AuthSessionResponse, LineAuthRequest } from '@arrows/shared';
import { SignJWT } from 'jose';

export interface AuthClient {
  getSessionToken(forceRefresh?: boolean): Promise<string | null>;
  invalidate(): void;
}

export type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** 同じ認証クライアントに同時到着したセッション再発行を 1 本へ集約する。 */
export class AuthRequestCoordinator<T> {
  private inFlight: Promise<T> | null = null;

  run(request: () => Promise<T>): Promise<T> {
    if (this.inFlight !== null) return this.inFlight;
    const current = Promise.resolve().then(request).finally(() => {
      if (this.inFlight === current) this.inFlight = null;
    });
    this.inFlight = current;
    return current;
  }
}

const devIssuer = 'arrows-dev-auth';
const developmentLineChannelId = '2011216360';

function secretKey(secret: string): Uint8Array {
  const key = new TextEncoder().encode(secret);
  if (key.byteLength < 32) throw new Error('VITE_AUTH_DEV_SECRET must contain at least 32 bytes.');
  return key;
}

function isAuthSessionResponse(value: unknown): value is AuthSessionResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<AuthSessionResponse>;
  return typeof candidate.sessionToken === 'string'
    && candidate.tokenType === 'Bearer'
    && typeof candidate.expiresIn === 'number';
}

export class DevAuthClient implements AuthClient {
  private session: { readonly token: string; readonly expiresAt: number } | null = null;
  private readonly requests = new AuthRequestCoordinator<string | null>();

  constructor(
    private readonly apiBase: string,
    private readonly secret: string,
    // 素の fetch を保持すると this がインスタンスになり、ブラウザでは Illegal invocation になる
    private readonly fetcher: Fetcher = (input, init) => fetch(input, init),
    private readonly now: () => number = Date.now,
    private readonly subject = 'browser-dev-client',
  ) {}

  invalidate(): void {
    this.session = null;
  }

  getSessionToken(forceRefresh = false): Promise<string | null> {
    if (!forceRefresh && this.session !== null && this.session.expiresAt > this.now() + 30_000) {
      return Promise.resolve(this.session.token);
    }
    return this.requests.run(() => this.refreshSession());
  }

  private async refreshSession(): Promise<string | null> {
    const issuedAt = Math.floor(this.now() / 1_000);
    const idToken = `dev.${await new SignJWT({})
      .setProtectedHeader({ alg: 'HS256', typ: 'JWT' })
      .setIssuer(devIssuer)
      .setAudience(developmentLineChannelId)
      .setSubject(this.subject)
      .setIssuedAt(issuedAt)
      .setExpirationTime(issuedAt + 300)
      .sign(secretKey(this.secret))}`;
    const body: LineAuthRequest = { idToken };
    const response = await this.fetcher(`${this.apiBase}/v1/auth/line`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) return null;
    const parsed: unknown = await response.json();
    if (!isAuthSessionResponse(parsed)) return null;
    this.session = {
      token: parsed.sessionToken,
      expiresAt: this.now() + parsed.expiresIn * 1_000,
    };
    return this.session.token;
  }
}
